import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface Artifact {
  src: string;
  size: number;
  rotation: number;
  speed: number;
  topPct: number;
  // Horizontal position as a percentage from the left edge of the artifact layer.
  leftPct: number;
  hideOnMobile?: boolean;
}

// Asset PNGs are exported at 2x for retina, so we halve the rendered size.
const ARTIFACTS: Artifact[] = [
  // Top region
  {
 src: '/assets/Frame-2.png', size: 34, rotation: 0, speed: 0.20, topPct: 2, leftPct: 9,
},
  {
 src: '/assets/Star 1.png', size: 36, rotation: 162, speed: 0.36, topPct: 4, leftPct: 30, hideOnMobile: true,
},
  {
 src: '/assets/Ellipse 9.png', size: 18, rotation: 180, speed: 0.50, topPct: 7, leftPct: 84,
},
  {
 src: '/assets/Ellipse 7.png', size: 29, rotation: -34, speed: 0.24, topPct: 11, leftPct: 81, hideOnMobile: true,
},
  {
 src: '/assets/Star 3.png', size: 22, rotation: 25, speed: 0.42, topPct: 13, leftPct: 5,
},

  // Upper-middle
  {
 src: '/assets/Star 4.png', size: 40, rotation: 17, speed: 0.30, topPct: 22, leftPct: 12,
},
  {
 src: '/assets/Frame-1.png', size: 36, rotation: 0, speed: 0.40, topPct: 28, leftPct: 84,
},
  {
 src: '/assets/Ellipse 7.png', size: 22, rotation: 90, speed: 0.18, topPct: 32, leftPct: 18, hideOnMobile: true,
},

  // Middle
  {
 src: '/assets/Star 1.png', size: 36, rotation: -24, speed: 0.22, topPct: 44, leftPct: 7,
},
  {
 src: '/assets/Frame.png', size: 36, rotation: 12, speed: 0.46, topPct: 50, leftPct: 90,
},
  {
 src: '/assets/Ellipse 11.png', size: 38, rotation: -146, speed: 0.28, topPct: 53, leftPct: 86,
},
  {
 src: '/assets/Star 5.png', size: 30, rotation: 17, speed: 0.34, topPct: 47, leftPct: 22, hideOnMobile: true,
},

  // Lower-middle
  {
 src: '/assets/Ellipse 12.png', size: 16, rotation: 0, speed: 0.55, topPct: 60, leftPct: 78,
},
  {
 src: '/assets/Frame-2.png', size: 28, rotation: 25, speed: 0.26, topPct: 66, leftPct: 8,
},
  {
 src: '/assets/Star 4.png', size: 32, rotation: -40, speed: 0.38, topPct: 70, leftPct: 88, hideOnMobile: true,
},

  // Bottom
  {
 src: '/assets/Ellipse 13.png', size: 30, rotation: -146, speed: 0.32, topPct: 80, leftPct: 86,
},
  {
 src: '/assets/Star 5.png', size: 44, rotation: 17, speed: 0.44, topPct: 86, leftPct: 12,
},
  {
 src: '/assets/Ellipse 12.png', size: 14, rotation: 180, speed: 0.50, topPct: 92, leftPct: 75, hideOnMobile: true,
},
  {
 src: '/assets/Star 1.png', size: 26, rotation: 80, speed: 0.20, topPct: 95, leftPct: 6,
},
];

function FloatingArtifacts() {
  const [scrollY, setScrollY] = useState(0);
  const rafId = useRef<number | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const handleScroll = () => {
      if (rafId.current !== null) return;
      rafId.current = window.requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafId.current = null;
      });
    };
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafId.current !== null) window.cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {ARTIFACTS.map((a, i) => {
        if (a.hideOnMobile && isMobile) return null;
        const sizePx = isMobile ? Math.round(a.size * 0.7) : a.size;
        const translateY = -scrollY * a.speed;
        return (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={`${a.src}-${i}`}
            component="img"
            src={a.src}
            alt=""
            sx={{
              position: 'absolute',
              top: `${a.topPct}%`,
              left: `${a.leftPct}%`,
              width: sizePx,
              height: sizePx,
              transform: `translate3d(-50%, ${translateY}px, 0) rotate(${a.rotation}deg)`,
              willChange: 'transform',
              opacity: 0.95,
            }}
          />
        );
      })}
    </Box>
  );
}

export default FloatingArtifacts;
